import React from "react";
import { ArrowLeft, LogOut, Volume2, VolumeX } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import LogoutButton from "@/components/LogoutButton";
import { toggleMuted, unlockAudio } from "@/lib/sounds";

/**
 * Bottom-left in-game toolbar: Back · Logout · Exit · Sound toggle.
 *
 * Pure presentational — all behaviour is provided via props. The wrapper
 * keeps the same absolute positioning that lived inline in GamePlay.jsx,
 * so visual output is unchanged.
 */
export default function GameToolbar({ onBack, onExit, muted, isDemoMode = false }) {
  const { t } = useI18n();
  return (
    <div className="absolute bottom-7 left-3 md:bottom-8 md:left-4 z-30 inline-flex items-center gap-2">
      <button
        data-testid="btn-back-menu"
        onClick={onBack}
        title={t("back_btn")}
        className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-full shadow-md border-b-2 border-amber-700 active:translate-y-[1px] active:border-b-0 px-4 py-1.5 text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>{t("back_btn")}</span>
      </button>
      {/* DEMO ("Aprender a Jogar"): Terminar sessão is hidden — the tutorial
          is anonymous, there is no session to end, and the Sair / Voltar
          buttons already exit cleanly without any confirmation modal. */}
      {!isDemoMode && <LogoutButton variant="inline" />}
      <button
        data-testid="btn-exit-game"
        onClick={onExit}
        className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-full shadow-md border-b-2 border-rose-700 active:translate-y-[1px] active:border-b-0 px-4 py-1.5 text-sm"
      >
        <LogOut className="w-4 h-4" />
        <span>{t("exit_btn")}</span>
      </button>
      <button
        data-testid="btn-toggle-sound"
        aria-label={muted ? t("audio_on") : t("audio_off")}
        title={muted ? t("audio_off") : t("audio_on")}
        onClick={() => { toggleMuted(); unlockAudio(); }}
        className={`inline-flex items-center gap-2 rounded-full shadow-md border-b-2 active:translate-y-[1px] active:border-b-0 px-4 py-1.5 text-sm font-bold ${muted ? "bg-slate-500 hover:bg-slate-600 border-slate-700 text-white" : "bg-emerald-500 hover:bg-emerald-600 border-emerald-700 text-white"}`}
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        <span>{muted ? t("audio_off") : t("audio_on")}</span>
      </button>
    </div>
  );
}
